import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { t, lang, tr } = useLang();
  const { login, sellerLogin, register, changePassword } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [seller, setSeller] = useState({ username: "", pin: "", remember: false });
  const [loading, setLoading] = useState(false);
  const [mustChange, setMustChange] = useState(null); // holds role after login if password change required
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [forgot, setForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success(data.message || tr("تم الإرسال إن كان البريد مسجّلاً لدينا", "Sent if the email is registered with us"));
      setForgot(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const redirect = (role) => {
    if (role === "owner") nav("/admin");
    else if (role === "seller") nav("/pos");
    else nav("/");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = mode === "login"
        ? await login(form.email, form.password)
        : await register(form.name, form.email, form.password);
      if (user.must_change_password) {
        setPw({ current: form.password, next: "", confirm: "" });
        setMustChange(user.role);
        toast(tr("يجب تغيير كلمة المرور عند أول دخول", "Please change your password on first login"));
        return;
      }
      toast.success(lang === "ar" ? "مرحباً بكِ" : "Welcome");
      redirect(user.role);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const submitChange = async (e) => {
    e.preventDefault();
    if (pw.next.length < 8) { toast.error(tr("كلمة المرور الجديدة 8 أحرف على الأقل", "New password must be at least 8 characters")); return; }
    if (pw.next !== pw.confirm) { toast.error(tr("كلمتا المرور غير متطابقتين", "Passwords do not match")); return; }
    setLoading(true);
    try {
      await changePassword(pw.current, pw.next);
      toast.success(tr("تم تحديث كلمة المرور", "Password updated"));
      redirect(mustChange);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const submitSeller = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(seller.pin)) { toast.error(tr("الرقم السري 6 أرقام", "PIN must be 6 digits")); return; }
    setLoading(true);
    try {
      const user = await sellerLogin(seller.username.trim(), seller.pin.trim(), seller.remember);
      toast.success(tr("مرحباً بكِ", "Welcome"));
      redirect(user.role);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const inp = "w-full border-b border-border bg-transparent py-3 text-sm focus:outline-none focus:border-primary";

  if (mode === "seller") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-background fade-in">
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-between mb-6 text-sm">
            <button onClick={() => setMode("login")} data-testid="seller-back" className="text-text-muted hover:text-primary">{tr("← دخول الأونر", "← Owner login")}</button>
            <button onClick={() => nav("/")} data-testid="seller-home" className="text-text-muted hover:text-primary">{tr("الرئيسية", "Home")}</button>
          </div>
          <img src="/logo.png" alt="RNF_Line" className="h-20 w-auto mx-auto mb-2" />
          <h1 className="text-center font-display text-2xl mb-1">{tr("دخول العاملات", "Staff login")}</h1>
          <p className="text-center text-text-muted text-xs mb-8">{tr("أدخلي اسم المستخدم والرقم السري (نقطة البيع)", "Enter your username and PIN (point of sale)")}</p>
          <form onSubmit={submitSeller} className="space-y-4">
            <input value={seller.username} onChange={(e) => setSeller({ ...seller, username: e.target.value })} required
              placeholder={tr("اسم المستخدم (مثال: rnf1)", "Username (e.g. rnf1)")} data-testid="seller-username" dir="ltr" className={inp} autoCapitalize="none" />
            <input type="password" inputMode="numeric" maxLength={6} value={seller.pin} onChange={(e) => setSeller({ ...seller, pin: e.target.value.replace(/\D/g, "") })} required
              placeholder={tr("الرقم السري (6 أرقام)", "PIN (6 digits)")} data-testid="seller-pin" dir="ltr" className={inp} />
            <label className="flex items-center gap-2 text-xs text-text-muted cursor-pointer">
              <input type="checkbox" checked={seller.remember} onChange={(e) => setSeller({ ...seller, remember: e.target.checked })} data-testid="seller-remember" />
              {tr("تذكّر هذا الجهاز (لابتوب المحل)", "Remember this device (store laptop)")}
            </label>
            <button type="submit" disabled={loading} data-testid="seller-login-btn" className="btn-primary w-full py-3.5 text-sm mt-4">
              {loading ? "…" : tr("دخول نقطة البيع", "Enter point of sale")}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (forgot) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-background fade-in">
        <div className="w-full max-w-sm">
          <img src="/logo.png" alt="RNF_Line" className="h-20 w-auto mx-auto mb-3" />
          <h1 className="text-center font-display text-2xl mb-1">{tr("استعادة كلمة المرور", "Reset password")}</h1>
          <p className="text-center text-text-muted text-xs mb-8">{tr("أدخلي بريدك الإلكتروني وسنرسل رابطاً آمناً ومؤقتاً لتعيين كلمة مرور جديدة.", "Enter your email and we'll send a secure, temporary link to set a new password.")}</p>
          <form onSubmit={submitForgot} className="space-y-4">
            <input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required
              placeholder={t("email")} data-testid="forgot-email" dir="ltr" className={inp} />
            <button type="submit" disabled={loading} data-testid="forgot-submit-btn" className="btn-primary w-full py-3.5 text-sm mt-4">
              {loading ? "…" : tr("إرسال رابط الاستعادة", "Send reset link")}
            </button>
          </form>
          <button onClick={() => setForgot(false)} data-testid="forgot-back" className="w-full text-center text-xs text-text-muted mt-6 hover:text-primary">{tr("العودة لتسجيل الدخول", "Back to login")}</button>
        </div>
      </div>
    );
  }

  if (mustChange) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-background fade-in">
        <div className="w-full max-w-sm">
          <img src="/logo.png" alt="RNF_Line" className="h-20 w-auto mx-auto mb-3" />
          <h1 className="text-center font-display text-2xl mb-1">{tr("تعيين كلمة مرور جديدة", "Set a new password")}</h1>
          <p className="text-center text-text-muted text-xs mb-8">{tr("لأمان حسابك، يرجى تعيين كلمة مرور خاصة بك.", "For your account's security, please set your own password.")}</p>
          <form onSubmit={submitChange} className="space-y-4">
            <input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} required
              placeholder={tr("كلمة المرور الجديدة", "New password")} data-testid="new-password" dir="ltr" className={inp} />
            <input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} required
              placeholder={tr("تأكيد كلمة المرور", "Confirm password")} data-testid="confirm-password" dir="ltr" className={inp} />
            <button type="submit" disabled={loading} data-testid="change-password-btn" className="btn-primary w-full py-3.5 text-sm mt-4">
              {loading ? "…" : tr("حفظ ومتابعة", "Save & continue")}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-background fade-in">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-between mb-6 text-sm">
          <button onClick={() => nav(-1)} data-testid="login-back" className="text-text-muted hover:text-primary">{tr("← رجوع", "← Back")}</button>
          <button onClick={() => nav("/")} data-testid="login-home" className="text-text-muted hover:text-primary">{tr("العودة إلى الرئيسية", "Back to home")}</button>
        </div>
        <img src="/logo.png" alt="RNF_Line" className="h-24 w-auto mx-auto mb-2" />
        <p className="text-center text-text-muted text-sm mb-10">{t("tagline")}</p>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
              placeholder={t("name")} data-testid="login-name" className={inp} />
          )}
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required
            placeholder={t("email")} data-testid="login-email" dir="ltr" className={inp} />
          <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required
            placeholder={t("password")} data-testid="login-password" dir="ltr" className={inp} />
          <button type="submit" disabled={loading} data-testid="login-submit-btn" className="btn-primary w-full py-3.5 text-sm tracking-wide mt-4">
            {loading ? "…" : mode === "login" ? t("login") : (lang === "ar" ? "إنشاء حساب" : "Register")}
          </button>
        </form>

        <button type="button" onClick={() => setForgot(true)} data-testid="forgot-link" className="w-full text-center text-xs text-accent mt-4 hover:underline">
          {tr("نسيت كلمة المرور؟", "Forgot password?")}
        </button>

        <button type="button" onClick={() => setMode("seller")} data-testid="seller-login-link" className="w-full text-center text-xs text-primary mt-4 hover:underline">
          {tr("دخول العاملات (نقطة البيع)", "Staff login (point of sale)")}
        </button>

        <button onClick={() => nav("/shop")} data-testid="login-guest" className="w-full text-center text-sm text-primary mt-6 underline underline-offset-4">
          {tr("متابعة التسوق بدون تسجيل", "Continue shopping as guest")}
        </button>

        <button onClick={() => setMode(mode === "login" ? "register" : "login")} data-testid="login-toggle-mode"
          className="w-full text-center text-xs text-text-muted mt-6 hover:text-primary">
          {mode === "login"
            ? (lang === "ar" ? "ليس لديكِ حساب؟ إنشاء حساب" : "No account? Register")
            : (lang === "ar" ? "لديكِ حساب؟ تسجيل الدخول" : "Have an account? Login")}
        </button>
      </div>
    </div>
  );
}
